import { escapePanelAttribute, indentPanelBlock } from "../panel-markdown"

interface TurndownRule {
	filter: (node: Node) => boolean
	replacement: (content: string, node: Node) => string
}

interface TurndownLike {
	addRule: (key: string, rule: TurndownRule) => void
}

const typeOf = (node: Node): string | null =>
	node.nodeType === 1 ? (node as HTMLElement).getAttribute("data-type") : null

const hasAttribute = (node: Node, name: string): boolean =>
	node.nodeType === 1 && (node as HTMLElement).hasAttribute(name)

// Turndown converts children before their parent, so the caption and hint
// come back to the frame as marked markdown, never as loose body text.
const CAPTION = ["\u0002", "\u0003"] as const
const HINT = ["\u0004", "\u0005"] as const

function takeMarked(
	content: string,
	[open, close]: readonly [string, string],
): { value: string; rest: string } {
	const start = content.indexOf(open)
	const end = content.indexOf(close, start + 1)
	if (start < 0 || end < 0) return { value: "", rest: content }
	return {
		value: content.slice(start + 1, end).trim(),
		rest: content.slice(0, start) + content.slice(end + 1),
	}
}

const oneLine = (markdown: string): string =>
	markdown.replace(/\s*\n+\s*/g, " ")

export function addFrameMarkdownRules(turndown: TurndownLike): void {
	turndown.addRule("mintlifyFrame", {
		filter: (node) => node.nodeName === "DIV" && typeOf(node) === "frame",
		replacement: (content) => {
			const caption = takeMarked(content, CAPTION)
			const hint = takeMarked(caption.rest, HINT)
			const props = [
				hint.value
					? ` hint="${escapePanelAttribute(oneLine(hint.value))}"`
					: "",
				caption.value
					? ` caption="${escapePanelAttribute(oneLine(caption.value))}"`
					: "",
			].join("")
			return `\n\n<Frame${props}>\n${indentPanelBlock(hint.rest.trim())}\n</Frame>\n\n`
		},
	})

	turndown.addRule("mintlifyFrameFigure", {
		filter: (node) =>
			node.nodeName === "FIGURE" && typeOf(node) === "frame-figure",
		replacement: (content) => content,
	})

	turndown.addRule("mintlifyFrameMedia", {
		filter: (node) =>
			node.nodeName === "DIV" && hasAttribute(node, "data-frame-media"),
		replacement: (content) => `\n\n${content.trim()}\n\n`,
	})

	turndown.addRule("mintlifyFrameCaption", {
		filter: (node) =>
			node.nodeName === "FIGCAPTION" &&
			hasAttribute(node, "data-frame-caption"),
		replacement: (content) => `${CAPTION[0]}${content}${CAPTION[1]}`,
	})

	turndown.addRule("mintlifyFrameHint", {
		filter: (node) =>
			node.nodeName === "P" && hasAttribute(node, "data-frame-hint"),
		replacement: (content) => `${HINT[0]}${content}${HINT[1]}`,
	})
}
